import Link from '@/components/Link/Link';
import Loading from '@/components/Loading/Loading';
import { useInvoice } from '@/hooks/invoice/useInvoice';
import { usePaymentSession } from '@/hooks/payment/usePaymentSession';
import { useConfig } from '@/hooks/useConfig';
import { formatPriceString } from '@/utils/numbers';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { referenceCodeSchema } from '../schemas';

const BankTransfer = () => {
  const navigate = useNavigate();
  const { urls } = useConfig();
  const { invoice, queryError, setInvoiceReferenceCode } = useInvoice();
  const { t } = useTranslation();
  const { partialPayment } = usePaymentSession();
  const [searchParams] = useSearchParams();
  const referenceCodeFromQuery = searchParams.get('ref');
  const amount = partialPayment.enabled
    ? partialPayment.amountPrice
    : formatPriceString(invoice?.remainingAmountInclVAT ?? 0);

  useEffect(() => {
    if (!referenceCodeFromQuery) return;

    const normalizedReferenceCode = referenceCodeFromQuery.toUpperCase();
    if (referenceCodeSchema.safeParse(normalizedReferenceCode).success) {
      setInvoiceReferenceCode(normalizedReferenceCode);
    } else {
      navigate('/pay/error', { replace: true });
    }
  }, [referenceCodeFromQuery]);

  useEffect(() => {
    // An invoice requested via ?ref= is still loading on the first renders
    if (referenceCodeFromQuery && !invoice && !queryError) return;

    if (!invoice || queryError) {
      navigate('/pay/error', { replace: true });
    }
  }, [invoice, queryError]);

  if (!invoice) {
    return <Loading height='50vh' />;
  }

  return (
    <Stack justifyContent='center' alignItems='center' gap={4}>
      <Typography component='h1' variant='h2' fontWeight='bold' textAlign='center'>
        {t('page.pay.pay-instructions.title')}
      </Typography>
      <Typography variant='body2' textAlign='center' sx={{ maxWidth: { xs: 'none', sm: '70%' } }}>
        {t('page.pay.pay-instructions.description')} <br /> {t('page.pay.pay-instructions.choose-one')}{' '}
        <Link href={`/pay/${invoice?.referenceCode}`}>{t('page.pay.pay-instructions.other-payment-method')}</Link>{' '}
        {t('page.pay.pay-instructions.to-pay-instantly')}
      </Typography>

      <Card sx={{ maxWidth: { xs: 'none', sm: '70%' } }}>
        <CardHeader title={t('components.payment-option-selector.total-amount')} subheader={amount} />

        <CardContent>
          <Stack gap={1}>
            <Stack direction='row' justifyContent='space-between'>
              <Typography variant='body2' fontWeight='bold'>
                {t('page.pay.pay-instructions.bankgiro-number')}
              </Typography>
              <Typography variant='body2'>{invoice?.bankGiroAccount}</Typography>
            </Stack>

            <Stack direction='row' justifyContent='space-between'>
              <Typography variant='body2' fontWeight='bold'>
                {t('page.pay.pay-instructions.ocr')}
              </Typography>
              <Typography variant='body2'>{invoice?.bankGiroOCR}</Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Typography variant='body2' textAlign='center'>
        {t('common-phrases.go-to')}{' '}
        <Link href={urls.myPages} target='_blank'>
          {t('common-phrases.my-pages')}
        </Link>{' '}
        {t('page.confirmation.to-download-invoice')}
      </Typography>
    </Stack>
  );
};

export default BankTransfer;
